/**
 * Dokumentum adatok generálása a Variable Registry alapján
 */

import { generateVariablesFromSources } from './variableRegistry';
import { Customer } from '../types/customer';
import { Product } from '../types/product';
import { Quotation } from '../types/quotation';

/**
 * Dokumentum adatok automatikus generálása
 */
export const generateDocumentData = (sources: {
  customer?: Customer;
  product?: Product;
  quotation?: Quotation;
  additionalData?: Record<string, string>;
}): Record<string, string> => {
  // Használjuk az új registry-t a változók generálásához
  const generatedVariables = generateVariablesFromSources(sources);
  
  // Visszakompatibilitás érdekében megtartjuk a régi kulcsokat is
  const legacyMapping: Record<string, string> = {};
  
  // Ügyfél adatok legacy mapping
  if (sources.customer) {
    const customer = sources.customer;
    
    // Régi kulcsok mapping
    legacyMapping['Cég megnevezése'] = generatedVariables['CUSTOMER_COMPANY_NAME'] || '';
    legacyMapping['Cég adószáma'] = generatedVariables['CUSTOMER_TAX_NUMBER'] || '';
    legacyMapping['Cégjegyzékszám'] = generatedVariables['CUSTOMER_REGISTRATION_NUMBER'] || '';
    legacyMapping['Cég címe'] = generatedVariables['CUSTOMER_ADDRESS'] || '';
    legacyMapping['Bankszámlaszáma'] = generatedVariables['CUSTOMER_BANK_ACCOUNT'] || '';
    legacyMapping['Kapcsolattartó neve'] = generatedVariables['CUSTOMER_CONTACT_NAME'] || '';
    legacyMapping['Kapcsolattartó e-mail'] = generatedVariables['CUSTOMER_CONTACT_EMAIL'] || '';
    legacyMapping['Kapcsolattartó telefon'] = generatedVariables['CUSTOMER_CONTACT_PHONE'] || '';
  }
  
  // Szerződés specifikus változók - használjuk a generált értékeket vagy alapértelmezetteket
  legacyMapping['Szerződés száma'] = generatedVariables['CONTRACT_NUMBER'] || `SZERZ-${Date.now()}`;
  legacyMapping['Szerződés kezdete'] = generatedVariables['CONTRACT_START_DATE'] || new Date().toISOString().split('T')[0];
  legacyMapping['Szerződés vége'] = generatedVariables['CONTRACT_END_DATE'] || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  legacyMapping['POD'] = generatedVariables['POD'] || '';
  legacyMapping['Kelt'] = generatedVariables['DOCUMENT_DATE'] || new Date().toLocaleDateString('hu-HU');
  legacyMapping['Dátum'] = generatedVariables['DOCUMENT_DATE'] || new Date().toLocaleDateString('hu-HU');
  
  // További hiányzó változók
  legacyMapping['Képviselő neve'] = generatedVariables['CUSTOMER_REPRESENTATIVE'] || '';
  legacyMapping['Kapcsolattartó fax'] = generatedVariables['CUSTOMER_CONTACT_FAX'] || '';
  legacyMapping['Pénzügyi kapcsolattartó neve'] = generatedVariables['CUSTOMER_FINANCE_CONTACT_NAME'] || '';
  legacyMapping['Pénzügyi kapcsolattartó e-mail'] = generatedVariables['CUSTOMER_FINANCE_CONTACT_EMAIL'] || '';
  legacyMapping['Pénzügyi kapcsolattartó telefon'] = generatedVariables['CUSTOMER_FINANCE_CONTACT_PHONE'] || '';
  legacyMapping['Fizetési határidő'] = generatedVariables['PAYMENT_DEADLINE'] || '';
  legacyMapping['Szerződéses ár'] = generatedVariables['CONTRACT_PRICE'] || '';
  legacyMapping['Valutanem'] = generatedVariables['CURRENCY'] || 'HUF';
  
  // Termék adatok legacy mapping
  if (sources.product) {
    legacyMapping['PRODUCT_NAME'] = generatedVariables['PRODUCT_NAME'] || '';
    legacyMapping['PRODUCT_CODE'] = generatedVariables['PRODUCT_CODE'] || '';
    legacyMapping['PRODUCT_DESCRIPTION'] = generatedVariables['PRODUCT_DESCRIPTION'] || '';
    legacyMapping['PRODUCT_NET_PRICE'] = generatedVariables['PRODUCT_NET_PRICE'] || '';
    legacyMapping['PRODUCT_GROSS_PRICE'] = generatedVariables['PRODUCT_GROSS_PRICE'] || '';
    legacyMapping['PRODUCT_TAX_RATE'] = generatedVariables['PRODUCT_TAX_RATE'] || '';
    legacyMapping['PRODUCT_CATEGORY'] = generatedVariables['PRODUCT_CATEGORY'] || '';
    
    // Csomag specifikus adatok
    if (sources.product.type === 'package') {
      legacyMapping['PACKAGE_SERVICES_LIST'] = generatedVariables['PACKAGE_SERVICES_LIST'] || '';
      legacyMapping['PACKAGE_SERVICES_TABLE'] = generatedVariables['PACKAGE_SERVICES_TABLE'] || '';
      legacyMapping['PACKAGE_TOTAL_HOURS'] = generatedVariables['PACKAGE_TOTAL_HOURS'] || '';
      legacyMapping['PACKAGE_ESTIMATED_DURATION'] = generatedVariables['PACKAGE_ESTIMATED_DURATION'] || '';
      legacyMapping['PACKAGE_METHODOLOGY'] = generatedVariables['PACKAGE_METHODOLOGY'] || '';
      legacyMapping['PACKAGE_DELIVERABLES'] = generatedVariables['PACKAGE_DELIVERABLES'] || '';
      legacyMapping['PACKAGE_REQUIREMENTS'] = generatedVariables['PACKAGE_REQUIREMENTS'] || '';
      legacyMapping['PACKAGE_TIMELINE'] = generatedVariables['PACKAGE_TIMELINE'] || '';
    }
  }
  
  // Árajánlat adatok legacy mapping
  if (sources.quotation) {
    legacyMapping['QUOTATION_NUMBER'] = generatedVariables['QUOTATION_NUMBER'] || '';
    legacyMapping['QUOTATION_DATE'] = generatedVariables['QUOTATION_DATE'] || '';
    legacyMapping['QUOTATION_VALID_UNTIL'] = generatedVariables['QUOTATION_VALID_UNTIL'] || '';
    legacyMapping['QUOTATION_PRICE'] = generatedVariables['QUOTATION_PRICE'] || '';
  }
  
  // Egyesítjük az új és régi változókat, az additionalData felülírja a többit
  return {
    ...generatedVariables,
    ...legacyMapping,
    ...(sources.additionalData || {})
  };
};

/**
 * Változó érték frissítése
 */
export const updateVariableValue = (
  variables: Record<string, string>,
  key: string,
  value: string
): Record<string, string> => {
  return {
    ...variables,
    [key]: value
  };
};

/**
 * Hiányzó kötelező változók ellenőrzése
 */
export const validateRequiredVariables = (
  variables: Record<string, string>
): { isValid: boolean; missingVariables: string[] } => {
  const missingVariables: string[] = [];
  
  // Itt ellenőrizhetjük a kötelező változókat
  const requiredKeys = [
    'CUSTOMER_COMPANY_NAME',
    'PRODUCT_NAME'
  ];
  
  requiredKeys.forEach(key => {
    if (!variables[key] || variables[key].trim() === '') {
      missingVariables.push(key);
    }
  });
  
  return {
    isValid: missingVariables.length === 0,
    missingVariables
  };
};
